import React, { useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import moment from 'moment-timezone';
import { ProcedureStep } from '@/types/ProcedureStep';
import { clearDirectProcedureStep, orTabletProcedureSteps, setDirectProcedureStep } from '@/graph/procedureSteps';
import { useScope } from '@/hooks/useScope';
import { directTimestampInstant, localTimestampInput } from '../util/directTimestamp';
import TimestampValueInput from './TimestampValueInput';

type Props = {
  step: ProcedureStep;
  patientId: number;
  patientName?: string;
  roomId: number;
  readOnly?: boolean;
};

type Draft = { instant: string; local: string; measurementId: number };

const conflictMessages: Record<string, string> = {
  ALREADY_CAPTURED: 'Surgeon Left OR was already recorded on another device. Refresh to see the latest time.',
  STALE_REVISION: 'Surgeon Left OR was changed on another device. Close and reopen to edit the latest time.',
  PATIENT_NOT_IN_ROOM: 'This patient is no longer the current patient in this OR.',
};

export const saveErrorMessage = (error: unknown): string => {
  const graphQLErrors: any[] = (error as any)?.graphQLErrors ?? [];
  const conflict = graphQLErrors.find(e => e?.message === 'DirectProcedureStepConflict');
  if (conflict) {
    const reason = conflict.extensions?.data?.reason ?? conflict.data?.reason;
    return conflictMessages[reason] ?? 'Surgeon Left OR changed. Close and reopen to try again.';
  }
  if (graphQLErrors.some(e => /future/i.test(e?.message ?? ''))) return 'The time cannot be in the future.';
  // Whole word, so messages such as "Failed to update" are not reported as a date change.
  if (graphQLErrors.some(e => /\bdate\b/i.test(e?.message ?? '')))
    return 'Only the time can be changed. The date must stay the same.';
  return 'Unable to save. Please try again.';
};

// Mousetrap binds the global K/L UNDO/Next to document keypress; keep dialog keys from reaching it.
// (No onKeyDown: MUI v4 Modal would replace its own Escape handler with it.)
const stopKey = (event: React.KeyboardEvent) => event.stopPropagation();

// Parent keys this component by patient/room, so a patient change dismisses a draft.
export default function SurgeonLeftOrButton({ step, patientId, patientName, roomId, readOnly }: Props) {
  const classes = useStyles();
  const client = useApolloClient();
  const scope = useScope();
  const timezone = scope?.hospital?.timezone?.id || 'UTC';
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [clearing, setClearing] = useState(false);
  const saving = useRef(false);
  const [save, { loading: saveLoading }] = useMutation(setDirectProcedureStep, {
    refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId } }],
    awaitRefetchQueries: true,
  });

  const [clear, { loading: clearLoading }] = useMutation(clearDirectProcedureStep, {
    refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId } }],
    awaitRefetchQueries: true,
  });
  const loading = saveLoading || clearLoading;

  // Time and revision come from one measurement row, so an edit never targets a revision it did not show.
  const current = step.measurement ?? null;
  const currentTime = current ? moment(current.timestamp).tz(timezone).format('HH:mm') : null;

  const close = () => {
    if (!saving.current) {
      setDraft(null);
      setError('');
    }
  };

  const persist = async (timestamp: string, measurementId: number | null) => {
    if (saving.current) return;
    saving.current = true;
    setError('');
    try {
      const result = await save({
        variables: {
          patientId,
          roomId,
          procedureStepType: step.type,
          timestamp,
          expectedMeasurementId: measurementId,
        },
      });
      if (!result.data?.setDirectProcedureStep) throw new Error('The timestamp could not be saved.');
      setDraft(null);
    } catch (e) {
      setError(saveErrorMessage(e));
      // Load the current value so closing and reopening starts from the latest revision.
      client
        .query({ query: orTabletProcedureSteps, variables: { patientId }, fetchPolicy: 'network-only' })
        .catch(() => undefined);
    } finally {
      saving.current = false;
    }
  };

  const captureOrEdit = () => {
    if (readOnly || loading || saving.current) return;
    setError('');
    setClearing(false);
    if (current) {
      setDraft({
        instant: current.timestamp,
        local: localTimestampInput(current.timestamp, timezone),
        measurementId: current.id,
      });
    } else {
      return persist(new Date().toISOString(), null);
    }
  };

  const saveEdit = async () => {
    if (!draft || saving.current) return;
    const timestamp = directTimestampInstant(draft.local, timezone, draft.instant);
    if (!timestamp) {
      setError('Enter a valid time.');
      return;
    }
    if (timestamp === draft.instant) {
      close();
      return;
    }
    await persist(timestamp, draft.measurementId);
  };

  const confirmClear = async () => {
    if (!draft?.measurementId || saving.current) return;
    saving.current = true;
    setError('');
    try {
      const result = await clear({
        variables: { patientId, roomId, procedureStepType: step.type, expectedMeasurementId: draft.measurementId },
      });
      if (!result.data?.clearDirectProcedureStep) throw new Error('The timestamp could not be cleared.');
      setDraft(null);
    } catch (e) {
      setError(saveErrorMessage(e));
      client
        .query({ query: orTabletProcedureSteps, variables: { patientId }, fetchPolicy: 'network-only' })
        .catch(() => undefined);
    } finally {
      saving.current = false;
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        className={classes.trigger}
        startIcon={current ? <CheckIcon /> : undefined}
        disabled={readOnly || loading}
        onClick={captureOrEdit}
        aria-label={current ? `Edit Surgeon Left OR time ${currentTime}` : 'Record Surgeon Left OR'}
      >
        Surgeon Left OR{current ? ` · ${currentTime}` : ''}
      </Button>
      {!draft && !!error && (
        <Typography color="error" role="alert">
          {error}
        </Typography>
      )}
      <Dialog
        open={!!draft}
        onClose={close}
        maxWidth="sm"
        fullWidth
        aria-labelledby="surgeon-left-or-title"
        PaperProps={{ className: classes.paper }}
        onKeyPress={stopKey}
        onKeyUp={stopKey}
      >
        <DialogTitle id="surgeon-left-or-title" disableTypography>
          <Typography variant="h4" align="center">
            {clearing ? 'Clear Surgeon Left OR time?' : 'Edit Surgeon Left OR'}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.content}>
          {patientName && (
            <Typography align="center" color="textSecondary" gutterBottom>
              {patientName}
            </Typography>
          )}
          {draft && !clearing ? (
            <Box className={classes.editTime}>
              <TimestampValueInput
                autoFocus
                aria-label="Time"
                type="time"
                value={draft.local}
                onClick={event => event.currentTarget.showPicker?.()}
                onChange={event => {
                  setDraft({ ...draft, local: event.target.value });
                  setError('');
                }}
                disabled={loading}
              />
            </Box>
          ) : (
            <Box className={classes.timeBox}>
              <Typography variant="h2" className={classes.time} align="center">
                {draft && moment(draft.instant).tz(timezone).format('HH:mm:ss')}
              </Typography>
              <Typography align="center" color="textSecondary">
                {draft && moment(draft.instant).tz(timezone).format('MMM D, YYYY')}
              </Typography>
            </Box>
          )}
          {clearing && <Typography align="center">This removes the recorded time. You can record it again.</Typography>}
          {draft && !clearing && (
            <Box className={classes.clearAction}>
              <Button
                color="secondary"
                disabled={loading}
                onClick={() => {
                  setClearing(true);
                  setError('');
                }}
              >
                Clear time
              </Button>
            </Box>
          )}
          {draft && !!error && (
            <Typography color="error" align="center" role="alert">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            variant="outlined"
            onClick={
              clearing
                ? () => {
                    if (!saving.current) {
                      setClearing(false);
                      setError('');
                    }
                  }
                : close
            }
            disabled={loading}
            className={classes.action}
          >
            {clearing ? 'No' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={clearing ? confirmClear : saveEdit}
            disabled={loading}
            className={classes.action}
          >
            {loading ? (clearing ? 'Clearing…' : 'Saving…') : clearing ? 'Yes, clear time' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  trigger: {
    marginTop: theme.spacing(1),
    minHeight: 44,
    padding: theme.spacing(0.75, 1.5),
    textTransform: 'none',
    textAlign: 'left',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
  },
  clearAction: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    '& button': { minHeight: 44, textTransform: 'none' },
  },
  content: { padding: theme.spacing(1, 2, 2) },
  timeBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },
  time: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums' as const,
    marginBottom: theme.spacing(1),
  },
  editTime: { marginTop: theme.spacing(2), display: 'flex', justifyContent: 'center' },
  actions: { padding: theme.spacing(1, 2, 2), gap: theme.spacing(1) },
  action: {
    flex: 1,
    minHeight: 52,
    fontSize: '1.1rem',
    textTransform: 'none',
    borderRadius: theme.shape.borderRadius * 2,
  },
}));
